import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import partNumberSchemas from "../info/partNumberSchemas";
import "./PartNumberBuilder.css";

import { collection, getDocs, limit, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { trackAnalyticsEvent } from "../analytics/siteAnalytics";

function normalize(value) {
  return value.replace(/\s+/g, "").toUpperCase();
}

function findExactKnownPartNumber(partNumber) {
  const clean = normalize(partNumber);

  for (const schema of partNumberSchemas) {
    const match = schema.knownPartNumbers?.find((item) => normalize(item.code) === clean);

    if (match) {
      return {
        schema,
        knownPartNumber: match,
      };
    }
  }

  return null;
}

function getKnownPartNumberMatch(partNumber) {
  const clean = normalize(partNumber);

  if (!clean) return null;

  for (const schema of partNumberSchemas) {
    const knownMatch = schema.knownPartNumbers?.find((item) => normalize(item.code) === clean);

    if (knownMatch) {
      return {
        schema,
        knownPartNumber: knownMatch,
      };
    }
  }

  return null;
}

function schemaCanAcceptPartNumber(schema, partNumber) {
  const clean = normalize(partNumber);

  if (!clean) return false;

  if (!clean.startsWith(schema.matchPrefix)) return false;

  // Lookup-only products usually do not live-match.
  // Exception: unique lookup-only prefixes like CORE.
  if (schema.lookupOnly && !schema.allowPrefixMatch) {
    return false;
  }

  for (let i = 0; i < clean.length; i++) {
    const position = schema.positions?.find((pos) => pos.index === i);

    if (!position) return false;

    const char = clean[i];
    const match = getOptionMatch(position, char);

    if (!match) return false;
  }

  return true;
}

function getPossibleSchemas(partNumber) {
  const clean = normalize(partNumber);

  if (!clean) return [];

  const exactKnownMatch = getKnownPartNumberMatch(clean);

  if (exactKnownMatch) {
    return [exactKnownMatch.schema];
  }

  const matches = partNumberSchemas.filter((schema) => schemaCanAcceptPartNumber(schema, clean));

  if (matches.length <= 1) return matches;

  const longestPrefixLength = Math.max(...matches.map((schema) => schema.matchPrefix.length));

  return matches.filter((schema) => schema.matchPrefix.length === longestPrefixLength);
}

function getOptionMatch(position, char) {
  if (!position || !char) return null;

  if (position.options === "A-Z") {
    return /^[A-Z]$/.test(char) ? { code: char, description: "Custom letter" } : null;
  }

  if (position.options === "0-9") {
    return /^[0-9]$/.test(char) ? { code: char, description: "Custom number" } : null;
  }

  if (!Array.isArray(position.options)) return null;

  return position.options.find((option) => option.code.toUpperCase() === char.toUpperCase());
}

function getGroupedValue(chars, groupedOption) {
  return groupedOption.indexes.map((index) => chars[index] || "").join("");
}

function validateGroupedOptions(chars, schema) {
  if (!schema?.groupedOptions) return [];

  return schema.groupedOptions.map((group) => {
    const value = getGroupedValue(chars, group);
    const isComplete = group.indexes.every((index) => chars[index]);

    if (!isComplete) {
      return {
        group: group.group,
        label: group.label,
        value,
        valid: true,
        complete: false,
        message: "",
      };
    }

    if (group.allowNumeric && /^\d+$/.test(value)) {
      return {
        group: group.group,
        label: group.label,
        value,
        valid: true,
        complete: true,
        message: group.description || "",
      };
    }

    if (Array.isArray(group.options)) {
      const match = group.options.find((option) => option.code.toUpperCase() === value.toUpperCase());

      return {
        group: group.group,
        label: group.label,
        value,
        valid: Boolean(match),
        complete: true,
        message: match ? match.description : `${value} is not a valid ${group.label}.`,
      };
    }

    return {
      group: group.group,
      label: group.label,
      value,
      valid: true,
      complete: true,
      message: group.description || "",
    };
  });
}

function getPositionStatus(chars, schema, index) {
  const char = chars[index];

  if (!char) {
    return {
      valid: true,
      filled: false,
      description: "",
    };
  }

  if (!schema) {
    return {
      valid: true,
      filled: true,
      description: "",
    };
  }

  const position = schema.positions.find((pos) => pos.index === index);

  if (!position) {
    return {
      valid: false,
      filled: true,
      description: "This position is not defined for this product.",
    };
  }

  const match = getOptionMatch(position, char);

  return {
    valid: Boolean(match),
    filled: true,
    description: match?.description || `"${char}" is not valid for ${position.label}.`,
  };
}

function buildInitialChars(length = 7) {
  return Array.from({ length }, () => "");
}

function getMergedPositions(schemas) {
  const map = new Map();

  schemas.forEach((schema) => {
    schema.positions?.forEach((position) => {
      if (!map.has(position.index)) {
        map.set(position.index, {
          index: position.index,
          labels: new Set(),
          group: position.group || null,
          options: new Map(),
          optionTypes: new Set(),
          schemas: [],
        });
      }

      const existing = map.get(position.index);

      existing.labels.add(position.label);
      existing.schemas.push(schema.productName);

      if (position.group) {
        existing.group = position.group;
      }

      if (position.options === "A-Z" || position.options === "0-9") {
        existing.optionTypes.add(position.options);
      }

      if (Array.isArray(position.options)) {
        position.options.forEach((option) => {
          const code = option.code.toUpperCase();

          if (!existing.options.has(code)) {
            existing.options.set(code, {
              code,
              description: option.description,
              usedBy: new Set(),
            });
          }

          existing.options.get(code).usedBy.add(schema.productName);
        });
      }
    });
  });

  return Array.from(map.values()).sort((a, b) => a.index - b.index);
}

function getMergedGroupedOptions(schemas, groupName) {
  const matchingGroups = schemas
    .map((schema) => schema.groupedOptions?.find((group) => group.group === groupName))
    .filter(Boolean);

  if (!matchingGroups.length) return null;

  const first = matchingGroups[0];
  const optionMap = new Map();

  matchingGroups.forEach((group) => {
    group.options?.forEach((option) => {
      const code = option.code.toUpperCase();

      if (!optionMap.has(code)) {
        optionMap.set(code, {
          code,
          description: option.description,
        });
      }
    });
  });

  return {
    ...first,
    options: Array.from(optionMap.values()),
  };
}

function getSlugFromProductPage(productPage) {
  if (!productPage) return "";

  return productPage
    .replace(/^https?:\/\/[^/]+/i, "")
    .replace(/^\/products\//, "")
    .replace(/^\//, "")
    .replace(/\/$/, "")
    .trim();
}

function getSchemasForProductSlug(productSlug) {
  const cleanSlug = String(productSlug || "")
    .replace(/^\/+|\/+$/g, "")
    .trim()
    .toLowerCase();

  if (!cleanSlug) return [];

  return partNumberSchemas.filter((schema) => getSlugFromProductPage(schema.productPage).toLowerCase() === cleanSlug);
}

function getSharedMatchPrefix(schemas) {
  const prefixes = schemas.map((schema) => normalize(schema.matchPrefix || "")).filter(Boolean);

  if (!prefixes.length) return "";

  let shared = prefixes[0];

  for (let i = 1; i < prefixes.length; i++) {
    while (shared && !prefixes[i].startsWith(shared)) {
      shared = shared.slice(0, -1);
    }
  }

  return shared;
}

function schemaMatchesRequestedProductInput(schema, partNumber) {
  const clean = normalize(partNumber);

  if (!clean) return true;

  const prefix = normalize(schema.matchPrefix || "");

  if (prefix && !clean.startsWith(prefix) && !prefix.startsWith(clean)) {
    return false;
  }

  if (!Array.isArray(schema.positions) || schema.positions.length === 0) {
    return schema.lookupOnly === true;
  }

  for (let i = 0; i < clean.length; i++) {
    const position = schema.positions.find((item) => item.index === i);

    if (!position) return false;

    const char = clean[i];

    if (position.options === "A-Z0-9") {
      if (!/^[A-Z0-9]$/.test(char)) return false;
      continue;
    }

    if (!getOptionMatch(position, char)) {
      return false;
    }
  }

  return true;
}

function formatProductTitle(schemas, limit = 2) {
  if (!schemas?.length) return "";

  const names = [...new Set(schemas.map((schema) => schema.productName))];
  const shown = names.slice(0, limit);
  const remaining = names.length - shown.length;

  if (remaining > 0) {
    return `${shown.join(" / ")} / or ${remaining} others`;
  }

  return shown.join(" / ");
}

export default function PartNumberBuilder() {
  const [chars, setChars] = useState(buildInitialChars(7));
  const [selectedSchemaId, setSelectedSchemaId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedProductSlug = (searchParams.get("product") || "").trim();

  const requestedPartNumber = normalize(searchParams.get("part") || "");
  const inputRefs = useRef([]);

  const stickyInputRefs = useRef([]);
  const topInputRowRef = useRef(null);
  const [showStickyBuilder, setShowStickyBuilder] = useState(false);
  const [activeProduct, setActiveProduct] = useState(null);
  const [copiedPartNumber, setCopiedPartNumber] = useState(false);
  const partAnalyticsStartedAtRef = useRef(null);
  const lastTrackedCompleteRef = useRef("");
  const lastTrackedInvalidRef = useRef("");

  const [showProductPicker, setShowProductPicker] = useState(false);
  const productPickerRef = useRef(null);
  const [productSearch, setProductSearch] = useState("");
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [selectedPickerSlug, setSelectedPickerSlug] = useState("");
  const [catalogLoading, setCatalogLoading] = useState(false);

  useEffect(() => {
    if (!showProductPicker) return;

    const handleClickOutside = (event) => {
      if (productPickerRef.current && !productPickerRef.current.contains(event.target)) {
        setShowProductPicker(false);
      }
    };

    document.addEventListener("pointerdown", handleClickOutside);

    return () => {
      document.removeEventListener("pointerdown", handleClickOutside);
    };
  }, [showProductPicker]);

  useEffect(() => {
    trackAnalyticsEvent("part_lookup_open", {
      tool: "part_number_lookup",
      entryMode: requestedPartNumber ? "prefilled_part" : requestedProductSlug ? "product_prefill" : "manual",
      requestedProductSlug,
      hasPrefilledPart: Boolean(requestedPartNumber),
    });
  }, []);
  const partNumber = chars.join("");
  const cleanPartNumber = normalize(partNumber);

  const requestedProductSchemas = useMemo(() => getSchemasForProductSlug(requestedProductSlug), [requestedProductSlug]);

  useEffect(() => {
    if (!requestedPartNumber) return;

    const exactKnown = getKnownPartNumberMatch(requestedPartNumber);

    const matches = exactKnown ? [exactKnown.schema] : getPossibleSchemas(requestedPartNumber);

    const maxLength =
      exactKnown?.schema?.maxLength ||
      (matches.length
        ? Math.max(...matches.map((item) => item.maxLength || requestedPartNumber.length))
        : requestedPartNumber.length);

    const next = buildInitialChars(Math.max(7, maxLength, requestedPartNumber.length));

    requestedPartNumber.split("").forEach((char, index) => {
      next[index] = char;
    });

    setSelectedSchemaId(matches.length === 1 ? matches[0].id : null);

    setChars(next.slice(0, maxLength));
  }, [requestedPartNumber]);

  useEffect(() => {
    if (requestedPartNumber || !requestedProductSchemas.length) {
      return;
    }

    const prefix = getSharedMatchPrefix(requestedProductSchemas);

    const next = buildInitialChars(Math.max(7, prefix.length));

    prefix.split("").forEach((char, index) => {
      next[index] = char;
    });

    setSelectedSchemaId(requestedProductSchemas.length === 1 ? requestedProductSchemas[0].id : null);

    setChars(next);
  }, [requestedProductSchemas, requestedPartNumber]);

  const requestedSchemaMatches = useMemo(() => {
    if (!requestedProductSchemas.length) return [];

    const matches = requestedProductSchemas.filter((schema) =>
      schemaMatchesRequestedProductInput(schema, cleanPartNumber)
    );

    return matches.length ? matches : requestedProductSchemas;
  }, [requestedProductSchemas, cleanPartNumber]);

  const possibleSchemaMatches = useMemo(() => getPossibleSchemas(cleanPartNumber), [cleanPartNumber]);

  useEffect(() => {
    const target = topInputRowRef.current;

    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowStickyBuilder(!entry.isIntersecting);
      },
      {
        root: null,
        threshold: 0.15,
      }
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadCatalog = async () => {
      setCatalogLoading(true);

      try {
        const snapshot = await getDocs(collection(db, "products"));

        if (cancelled) return;

        setCatalogProducts(
          snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
        );
      } catch (error) {
        console.error("Error loading product catalog:", error);
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    };

    loadCatalog();

    return () => {
      cancelled = true;
    };
  }, []);

  const configurableProducts = useMemo(() => {
    const productsBySlug = new Map();

    catalogProducts.forEach((product) => {
      const slug = getSlugFromProductPage(product.slug || "");

      if (slug) {
        productsBySlug.set(slug, product);
      }
    });

    const products = new Map();

    partNumberSchemas.forEach((schema) => {
      const slug = getSlugFromProductPage(schema.productPage);

      if (!slug) return;

      const product = productsBySlug.get(slug);

      // Read taxonomy from the matching Firebase product.
      // These are candidate field names; use whichever your
      // product documents actually contain.
      const familyValue =
        product?.familyName ||
        product?.productFamily ||
        product?.family ||
        product?.category ||
        product?.categories?.[0];

      const family =
        typeof familyValue === "string" ? familyValue : familyValue?.name || familyValue?.title || "Other Products";

      if (!products.has(slug)) {
        products.set(slug, {
          slug,
          productName: product?.product || product?.name || schema.productName,
          family,
          schemas: [],
        });
      }

      products.get(slug).schemas.push(schema);
    });

    return Array.from(products.values()).sort((a, b) => a.productName.localeCompare(b.productName));
  }, [catalogProducts]);

  const groupedCatalog = useMemo(() => {
    // Match the Go!Foton mega menu category order.
    const familyOrder = [
      "Fiber Cable Assemblies",
      "Fiber Panels",
      "Cassettes",
      "Fiber Terminals & Closures",
      "Fiber Network Monitoring",
      "Frames",
      "Transceivers",
      "Passive Optical Components",
      "Photodetectors",
      "Collimators & Probes",
      "Lenses",
    ];

    const groups = {};

    const search = productSearch.trim().toLowerCase();

    configurableProducts
      .filter(
        (product) => product.productName.toLowerCase().includes(search) || product.family.toLowerCase().includes(search)
      )
      .forEach((product) => {
        if (!groups[product.family]) {
          groups[product.family] = [];
        }

        groups[product.family].push(product);
      });

    return Object.entries(groups).sort(([familyA], [familyB]) => {
      const indexA = familyOrder.findIndex((name) => name.toLowerCase() === familyA.toLowerCase());

      const indexB = familyOrder.findIndex((name) => name.toLowerCase() === familyB.toLowerCase());

      // Categories not in the mega menu appear at the bottom.
      if (indexA === -1 && indexB === -1) {
        return familyA.localeCompare(familyB);
      }

      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });
  }, [configurableProducts, productSearch]);

  const selectedPickerProduct = configurableProducts.find((product) => product.slug === selectedPickerSlug);

  const selectedKnownPartNumbers = selectedPickerProduct
    ? Array.from(
        new Map(
          selectedPickerProduct.schemas.flatMap((schema) =>
            (schema.knownPartNumbers || []).map((part) => [normalize(part.code), part])
          )
        ).values()
      )
    : [];

  const selectedProductIsLookupOnly =
    !!selectedPickerProduct && selectedPickerProduct.schemas.every((schema) => schema.lookupOnly === true);

  const handleCopyPartNumber = async () => {
    if (!cleanPartNumber) return;

    try {
      await navigator.clipboard.writeText(cleanPartNumber);

      trackAnalyticsEvent("part_lookup_copy", {
        tool: "part_number_lookup",
        partNumber: cleanPartNumber,
        productName: schema?.productName || "",
        schemaId: schema?.id || "",
      });

      setCopiedPartNumber(true);

      setTimeout(() => {
        setCopiedPartNumber(false);
      }, 1400);
    } catch (error) {
      console.error("Failed to copy part number:", error);
    }
  };

  const schema = useMemo(() => {
    if (!cleanPartNumber) {
      return null;
    }

    const exactKnown = getKnownPartNumberMatch(cleanPartNumber);

    if (exactKnown) {
      return exactKnown.schema;
    }

    if (selectedSchemaId) {
      const requestedSelected = requestedSchemaMatches.find((item) => item.id === selectedSchemaId);

      if (requestedSelected) {
        return requestedSelected;
      }

      return possibleSchemaMatches.find((item) => item.id === selectedSchemaId) || null;
    }

    if (requestedSchemaMatches.length === 1) {
      return requestedSchemaMatches[0];
    }

    if (possibleSchemaMatches.length === 1) {
      return possibleSchemaMatches[0];
    }

    return null;
  }, [cleanPartNumber, possibleSchemaMatches, requestedSchemaMatches, selectedSchemaId]);

  const activeSchemas = useMemo(() => {
    // No characters = no active product or configuration options.
    if (!cleanPartNumber) return [];

    if (schema) return [schema];

    if (requestedSchemaMatches.length > 0) {
      return requestedSchemaMatches;
    }

    return possibleSchemaMatches;
  }, [cleanPartNumber, schema, requestedSchemaMatches, possibleSchemaMatches]);

  const activeSchemaForProduct = useMemo(() => {
    if (schema) return schema;

    if (activeSchemas.length === 1) {
      return activeSchemas[0];
    }

    const productPages = [...new Set(activeSchemas.map((item) => item.productPage).filter(Boolean))];

    if (productPages.length === 1) {
      return activeSchemas[0];
    }

    return null;
  }, [schema, activeSchemas]);

  useEffect(() => {
    const fetchActiveProduct = async () => {
      const slug = getSlugFromProductPage(activeSchemaForProduct?.productPage);

      if (!slug) {
        setActiveProduct(null);
        return;
      }

      try {
        const q = query(collection(db, "products"), where("slug", "==", slug), limit(1));

        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          setActiveProduct(null);
          return;
        }

        const docSnap = snapshot.docs[0];

        setActiveProduct({
          id: docSnap.id,
          ...docSnap.data(),
        });
      } catch (error) {
        console.error("Error fetching product image:", error);
        setActiveProduct(null);
      }
    };

    fetchActiveProduct();
  }, [activeSchemaForProduct]);

  const pageTitle = useMemo(() => {
    if (!cleanPartNumber) {
      return "Start typing a part number";
    }

    if (schema) {
      return schema.productName;
    }

    if (activeSchemas.length > 1) {
      return formatProductTitle(activeSchemas, 2);
    }

    return "No matching product found";
  }, [cleanPartNumber, schema, activeSchemas, requestedSchemaMatches]);

  const mergedPositions = useMemo(() => getMergedPositions(activeSchemas), [activeSchemas]);

  const exactKnownPartNumber = useMemo(() => {
    return getKnownPartNumberMatch(cleanPartNumber)?.knownPartNumber || null;
  }, [cleanPartNumber]);

  const visibleLength = useMemo(() => {
    if (schema) return Math.max(schema.minLength || 7, chars.length);

    if (activeSchemas.length > 0) {
      const maxMinLength = Math.max(...activeSchemas.map((item) => item.minLength || 7));

      return Math.max(maxMinLength, chars.length);
    }

    return Math.max(7, chars.length);
  }, [schema, activeSchemas, chars.length]);

  const visibleChars = useMemo(() => {
    const maxLength = schema
      ? schema.maxLength
      : activeSchemas.length > 0
        ? Math.max(...activeSchemas.map((item) => item.maxLength || 7))
        : visibleLength;

    const targetLength = Math.min(visibleLength, maxLength);

    return Array.from({ length: targetLength }, (_, index) => chars[index] || "");
  }, [chars, visibleLength, schema, activeSchemas]);

  useEffect(() => {
    if (!cleanPartNumber) {
      partAnalyticsStartedAtRef.current = null;
      lastTrackedCompleteRef.current = "";
      lastTrackedInvalidRef.current = "";
      return;
    }

    if (!partAnalyticsStartedAtRef.current) {
      partAnalyticsStartedAtRef.current = Date.now();

      trackAnalyticsEvent("part_lookup_start", {
        tool: "part_number_lookup",
        entryMode: requestedPartNumber ? "prefilled_part" : requestedProductSlug ? "product_prefill" : "manual",
        requestedProductSlug,
        startingLength: cleanPartNumber.length,
      });
    }
  }, [cleanPartNumber, requestedPartNumber, requestedProductSlug]);

  const groupedValidation = useMemo(() => validateGroupedOptions(chars, schema), [chars, schema]);

  const hasInvalidGroup = groupedValidation.some((item) => item.valid === false);

  const hasInvalidCharacter = useMemo(() => {
    if (!schema) return false;

    return chars.some((char, index) => {
      if (!char) return false;
      return !getPositionStatus(chars, schema, index).valid;
    });
  }, [chars, schema]);

  const isInvalid = hasInvalidCharacter || hasInvalidGroup;

  useEffect(() => {
    if (!cleanPartNumber || !schema) return;

    const expectedLength = schema.maxLength || schema.minLength || cleanPartNumber.length;

    const isFullLength = cleanPartNumber.length >= expectedLength;

    if (!isFullLength) return;

    if (!isInvalid) {
      if (lastTrackedCompleteRef.current === cleanPartNumber) {
        return;
      }

      lastTrackedCompleteRef.current = cleanPartNumber;

      const durationMs = partAnalyticsStartedAtRef.current ? Date.now() - partAnalyticsStartedAtRef.current : null;

      trackAnalyticsEvent("part_lookup_complete", {
        tool: "part_number_lookup",
        partNumber: cleanPartNumber,
        productName: schema.productName || "",
        schemaId: schema.id || "",
        productPage: schema.productPage || "",
        requestedProductSlug,
        exactKnown: Boolean(exactKnownPartNumber),
        durationMs,
        entryMode: requestedPartNumber ? "prefilled_part" : requestedProductSlug ? "product_prefill" : "manual",
      });

      return;
    }

    if (lastTrackedInvalidRef.current === cleanPartNumber) {
      return;
    }

    lastTrackedInvalidRef.current = cleanPartNumber;

    trackAnalyticsEvent("part_lookup_invalid", {
      tool: "part_number_lookup",
      partNumberLength: cleanPartNumber.length,
      schemaId: schema.id || "",
      productName: schema.productName || "",
      requestedProductSlug,
    });
  }, [cleanPartNumber, schema, isInvalid, exactKnownPartNumber, requestedPartNumber, requestedProductSlug]);

  const handlePaste = (index, event, options = {}) => {
    const { useStickyFocus = false } = options;

    const pastedText = event.clipboardData.getData("text");
    const cleanValue = normalize(pastedText);

    if (!cleanValue) return;

    event.preventDefault();

    setChars((prev) => {
      const next = [...prev];

      cleanValue.split("").forEach((char, offset) => {
        const targetIndex = index + offset;
        next[targetIndex] = char;
      });

      const nextPartNumber = next.join("");
      const matchedSchemas = getPossibleSchemas(nextPartNumber);
      const exactKnown = getKnownPartNumberMatch(nextPartNumber);

      const maxLength =
        exactKnown?.schema?.maxLength ||
        (matchedSchemas.length > 0
          ? Math.max(...matchedSchemas.map((item) => item.maxLength || 7))
          : Math.max(7, index + cleanValue.length));

      while (next.length < maxLength) {
        next.push("");
      }

      return next.slice(0, maxLength);
    });

    setTimeout(() => {
      const refs = useStickyFocus ? stickyInputRefs : inputRefs;
      const focusIndex = Math.min(index + cleanValue.length - 1, refs.current.length - 1);

      refs.current[focusIndex]?.focus({ preventScroll: true });
      refs.current[focusIndex]?.select?.();
    }, 0);
  };

  const updateChar = (index, value, options = {}) => {
    const { focusNext = true, useStickyFocus = false } = options;
    const cleanValue = normalize(value);

    if (!cleanValue) return;

    setChars((prev) => {
      const next = [...prev];

      // If user pastes a full or partial part number, spread it across boxes.
      if (cleanValue.length > 1) {
        cleanValue.split("").forEach((char, offset) => {
          const targetIndex = index + offset;
          next[targetIndex] = char;
        });

        const nextPartNumber = next.join("");
        const matchedSchemas = getPossibleSchemas(nextPartNumber);
        const exactKnown = getKnownPartNumberMatch(nextPartNumber);

        const maxLength =
          exactKnown?.schema?.maxLength ||
          (matchedSchemas.length > 0
            ? Math.max(...matchedSchemas.map((item) => item.maxLength || 7))
            : Math.max(7, next.length));

        while (next.length < Math.min(maxLength, index + cleanValue.length)) {
          next.push("");
        }

        return next.slice(0, maxLength);
      }

      // Normal single-character typing.
      next[index] = cleanValue.slice(-1);

      const nextPartNumber = next.join("");
      const matchedSchemas = getPossibleSchemas(nextPartNumber);
      const exactKnown = getKnownPartNumberMatch(nextPartNumber);

      const maxLength =
        exactKnown?.schema?.maxLength ||
        (matchedSchemas.length > 0
          ? Math.max(...matchedSchemas.map((item) => item.maxLength || 7))
          : Math.max(7, next.length + 1));

      if (index === next.length - 1 && next.length < maxLength) {
        next.push("");
      }

      return next.slice(0, maxLength);
    });

    if (focusNext) {
      setTimeout(() => {
        const refs = useStickyFocus ? stickyInputRefs : inputRefs;
        const nextFocusIndex = Math.min(index + cleanValue.length, inputRefs.current.length - 1);

        refs.current[nextFocusIndex]?.focus({ preventScroll: true });
      }, 0);
    }
  };

  const handleKeyDown = (index, event, options = {}) => {
    const { useStickyFocus = false } = options;
    const refs = useStickyFocus ? stickyInputRefs : inputRefs;

    if (event.key === "Backspace") {
      event.preventDefault();

      let focusIndex = index;

      setChars((prev) => {
        const next = [...prev];

        if (next[index]) {
          next[index] = "";
          focusIndex = index;
        } else if (index > 0) {
          next[index - 1] = "";
          focusIndex = index - 1;
        }

        while (next.length > 7 && next[next.length - 1] === "") {
          const maxRequiredLength = schema?.minLength || 7;

          if (next.length <= maxRequiredLength) break;

          if (focusIndex >= next.length - 1) {
            focusIndex = Math.max(maxRequiredLength - 1, next.length - 2);
          }

          next.pop();
        }

        return next;
      });

      setTimeout(() => {
        refs.current[focusIndex]?.focus({ preventScroll: true });
        refs.current[focusIndex]?.select?.();
      }, 0);

      return;
    }

    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      refs.current[index - 1]?.focus({ preventScroll: true });
      refs.current[index - 1]?.select?.();
      return;
    }

    if (event.key === "ArrowRight" && index < visibleChars.length - 1) {
      event.preventDefault();
      refs.current[index + 1]?.focus({ preventScroll: true });
      refs.current[index + 1]?.select?.();
      return;
    }

    // Fix: allow typing over an existing filled box.
    // Without this, maxLength={1} can block typing when focus lands on a filled previous box.
    if (
      event.key.length === 1 &&
      /^[a-zA-Z0-9_-]$/.test(event.key) &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.altKey
    ) {
      event.preventDefault();

      updateChar(index, event.key, {
        focusNext: true,
        useStickyFocus,
      });

      return;
    }
  };

  const handleOptionClick = (positionIndex, code) => {
    trackAnalyticsEvent("part_lookup_option_select", {
      tool: "part_number_lookup",
      optionType: "position",
      positionIndex: positionIndex + 1,
      code,
      productName: schema?.productName || "",
      schemaId: schema?.id || "",
    });

    setChars((prev) => {
      const next = [...prev];

      next[positionIndex] = code.toUpperCase();

      const nextPartNumber = next.join("");
      const matchedSchemas = getPossibleSchemas(nextPartNumber);

      const targetLength =
        matchedSchemas.length > 0 ? Math.max(...matchedSchemas.map((item) => item.maxLength || 7)) : next.length;

      while (next.length < Math.min(targetLength, positionIndex + 2)) {
        next.push("");
      }

      return next;
    });

    // Do NOT focus the top input here.
    // Clicking configuration options should not scroll the user back to the top.
  };

  const handleGroupedOptionClick = (group, code) => {
    trackAnalyticsEvent("part_lookup_option_select", {
      tool: "part_number_lookup",
      optionType: "group",
      group: group?.group || "",
      label: group?.label || "",
      code,
      productName: schema?.productName || "",
      schemaId: schema?.id || "",
    });

    setChars((prev) => {
      const next = [...prev];
      const pieces = code.toUpperCase().split("");

      group.indexes.forEach((index, i) => {
        next[index] = pieces[i] || "";
      });

      return next;
    });

    // Do NOT focus the top input here.
  };

  const clearBuilder = () => {
    trackAnalyticsEvent("part_lookup_clear", {
      tool: "part_number_lookup",
      partNumberLength: cleanPartNumber.length,
      productName: schema?.productName || "",
      schemaId: schema?.id || "",
    });

    // Clear the selected product and schema.
    setSelectedSchemaId(null);
    setSelectedPickerSlug("");
    setShowProductPicker(false);
    setProductSearch("");

    // Clear all part-number inputs.
    setChars(buildInitialChars(7));

    // Remove product and part-number URL parameters
    // so the prefill effects do not restore the old product.
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("product");
    nextParams.delete("part");

    setSearchParams(nextParams, { replace: true });

    // Return focus to the first input without scrolling.
    setTimeout(() => {
      inputRefs.current[0]?.focus({ preventScroll: true });
    }, 0);
  };

  const setBuilderPartNumber = (code, schemaId = null, maxLength = null) => {
    const clean = normalize(code || "");

    const nextLength = Math.max(7, clean.length, maxLength || 0);

    const next = buildInitialChars(nextLength);

    clean.split("").forEach((char, index) => {
      next[index] = char;
    });

    setSelectedSchemaId(schemaId);
    setChars(next);
  };

  const handleSelectProduct = (slug) => {
    const product = configurableProducts.find((item) => item.slug === slug);

    if (!product) return;

    setSelectedPickerSlug(slug);

    const schemas = product.schemas;

    const lookupOnly = schemas.every((item) => item.lookupOnly === true);

    const knownParts = Array.from(
      new Map(
        schemas.flatMap((item) => (item.knownPartNumbers || []).map((part) => [normalize(part.code), part]))
      ).values()
    );

    // Multiple fixed part numbers:
    // leave popup open for the customer to choose one.
    if (lookupOnly && knownParts.length > 1) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);

    // Remove stale part number from an earlier selection.
    nextParams.delete("part");

    // Use the existing product-prefill system.
    nextParams.set("product", slug);

    // One fixed part number: prefill the complete code.
    if (lookupOnly && knownParts.length === 1) {
      nextParams.set("part", knownParts[0].code);
    }

    // Clear previous schema selection before changing products.
    setSelectedSchemaId(null);

    // Existing effects will populate the inputs.
    setSearchParams(nextParams, { replace: true });

    setShowProductPicker(false);
  };

  const handleSelectKnownPartNumber = (code) => {
    if (!code) return;

    const nextParams = new URLSearchParams(searchParams);

    nextParams.set("part", normalize(code));

    if (selectedPickerSlug) {
      nextParams.set("product", selectedPickerSlug);
    }

    setSelectedSchemaId(null);
    setSearchParams(nextParams, { replace: true });
    setShowProductPicker(false);
  };

  const stickyBoxWidth = 38;
  const stickyBoxGap = 7;
  const stickyBoxesPixelWidth =
    visibleChars.length * stickyBoxWidth + Math.max(visibleChars.length - 1, 0) * stickyBoxGap;

  return (
    <main className="part-builder-page">
      <section className="part-builder-hero">
        <h2>Part Number Builder</h2>
        <p>Enter a Go!Foton part number to decode it, or select options below to build a valid configuration.</p>
      </section>

      <section className="part-builder-card">
        <div className="part-builder-topRow">
          <div>
            <div className="part-builder-titleRow">
              <h2 className="part-builder-title">{pageTitle}</h2>

              {!cleanPartNumber && (
                <div className="part-builder-pickerAnchor" ref={productPickerRef}>
                  <button
                    type="button"
                    className="part-builder-configureButton"
                    onClick={() => setShowProductPicker((prev) => !prev)}
                    aria-expanded={showProductPicker}
                    aria-haspopup="dialog"
                  >
                    Or configure a product
                    <svg
                      className={`part-builder-chevron ${showProductPicker ? "is-open" : ""}`}
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </button>

                  {showProductPicker && (
                    <div className="part-builder-pickerPopup" role="dialog" aria-label="Choose a product">
                      <div className="part-builder-pickerHeader">
                        <strong>Choose a Product</strong>

                        <button
                          type="button"
                          className="part-builder-pickerClose"
                          onClick={() => setShowProductPicker(false)}
                          aria-label="Close product selector"
                        >
                          ×
                        </button>
                      </div>

                      <div className="part-builder-pickerSearchWrap">
                        <input
                          autoFocus
                          type="search"
                          className="part-builder-pickerSearch"
                          placeholder="Search products..."
                          value={productSearch}
                          onChange={(event) => setProductSearch(event.target.value)}
                        />
                      </div>

                      <div className="part-builder-pickerList">
                        {catalogLoading && <p className="part-builder-pickerEmpty">Loading products...</p>}

                        {!catalogLoading && groupedCatalog.length === 0 && (
                          <p className="part-builder-pickerEmpty">No matching products found.</p>
                        )}

                        {groupedCatalog.map(([family, products]) => (
                          <div className="part-builder-pickerGroup" key={family}>
                            <div className="part-builder-pickerGroupTitle">{family}</div>

                            {products.map((product) => (
                              <button
                                type="button"
                                key={product.slug}
                                className={`part-builder-pickerProduct ${
                                  selectedPickerSlug === product.slug ? "is-selected" : ""
                                }`}
                                onClick={() => handleSelectProduct(product.slug)}
                              >
                                <span>{product.productName}</span>
                              </button>
                            ))}
                          </div>
                        ))}
                      </div>

                      {selectedProductIsLookupOnly && selectedKnownPartNumbers.length > 1 && (
                        <div className="part-builder-pickerKnown">
                          <strong>Available Part Numbers</strong>
                          <p>This product has fixed part numbers and cannot be custom configured.</p>

                          {selectedKnownPartNumbers.map((part) => (
                            <button
                              type="button"
                              key={part.code}
                              onClick={() => {
                                handleSelectKnownPartNumber(part.code);
                                setShowProductPicker(false);
                              }}
                            >
                              {part.code}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {schema ? (
              <div className="part-builder-links">
                {schema.productPage && (
                  <a
                    href={schema.productPage}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() =>
                      trackAnalyticsEvent("part_lookup_product_click", {
                        tool: "part_number_lookup",
                        partNumber: cleanPartNumber,
                        productName: schema.productName || "",
                        schemaId: schema.id || "",
                        destination: schema.productPage,
                      })
                    }
                  >
                    Product Page
                  </a>
                )}

                {schema.datasheet && (
                  <a
                    href={schema.datasheet}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() =>
                      trackAnalyticsEvent("part_lookup_datasheet_click", {
                        tool: "part_number_lookup",
                        partNumber: cleanPartNumber,
                        productName: schema.productName || "",
                        schemaId: schema.id || "",
                        destination: schema.datasheet,
                      })
                    }
                  >
                    Datasheet
                  </a>
                )}
              </div>
            ) : (
              <p className="part-builder-muted">
                Example: type <strong>P</strong> to detect the PEACOC family.
              </p>
            )}
          </div>

          <button type="button" className="part-builder-clear" onClick={clearBuilder}>
            Clear
          </button>
        </div>

        <div className="part-builder-inputRow" ref={topInputRowRef}>
          {visibleChars.map((char, index) => {
            const status = getPositionStatus(chars, schema, index);
            const position = schema?.positions?.find((pos) => pos.index === index);

            return (
              <div className="part-builder-inputGroup" key={index}>
                <input
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  className={`part-builder-box ${
                    status.filled && !status.valid && !exactKnownPartNumber ? "is-invalid" : ""
                  }`}
                  value={char}
                  maxLength={1}
                  onPaste={(event) => handlePaste(index, event)}
                  onChange={(event) => updateChar(index, event.target.value)}
                  onKeyDown={(event) => handleKeyDown(index, event)}
                  aria-label={position?.label || `Character ${index + 1}`}
                />

                <div className="part-builder-positionNumber">{index + 1}</div>

                {position?.label && <div className="part-builder-positionLabel">{position.label}</div>}
              </div>
            );
          })}
        </div>

        <div className="part-builder-resultRow">
          <div className="part-builder-result">
            <span>Current Part Number:</span>
            <strong>{cleanPartNumber || "—"}</strong>
          </div>

          <button
            type="button"
            className="part-builder-copyButton"
            onClick={handleCopyPartNumber}
            disabled={!cleanPartNumber}
            title="Copy part number"
          >
            <span className="part-builder-copyIcon">⧉</span>
            {copiedPartNumber ? "Copied" : "Copy"}
          </button>
        </div>

        {schema && isInvalid && !exactKnownPartNumber && (
          <div className="part-builder-error">This part number does not match the available configuration.</div>
        )}

        {schema?.notes?.length > 0 && (
          <div className="part-builder-notes">
            {schema.notes.map((note, index) => (
              <p key={index}>{note}</p>
            ))}
          </div>
        )}
      </section>

      {activeSchemas.length > 0 && !activeSchemas.every((item) => item.lookupOnly) && (
        <section className="part-builder-options">
          <h2>Configuration Options</h2>

          {!schema && activeSchemas.length > 1 && (
            <div className="part-builder-possibleMatches">
              <h3>Matching Product Formats</h3>
              <p className="part-builder-muted">
                This part number currently matches more than one product format. The shared fields are shown below.
                Choose one product only if you want to narrow the options.
              </p>

              <div className="part-builder-matchGrid">
                {activeSchemas.map((possibleSchema) => (
                  <button
                    type="button"
                    key={possibleSchema.id}
                    className="part-builder-matchButton"
                    onClick={() => {
                      setSelectedSchemaId(possibleSchema.id);

                      trackAnalyticsEvent("part_lookup_schema_select", {
                        tool: "part_number_lookup",
                        schemaId: possibleSchema.id,
                        productName: possibleSchema.productName,
                        currentLength: cleanPartNumber.length,
                      });
                    }}
                  >
                    <strong>{possibleSchema.productName}</strong>
                    <span>Prefix: {possibleSchema.matchPrefix}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {mergedPositions.map((position) => {
            const currentChar = chars[position.index];
            const labels = Array.from(position.labels);
            const label = labels.join(" / ");
            const options = Array.from(position.options.values());
            const group = position.group ? getMergedGroupedOptions(activeSchemas, position.group) : null;

            if (position.group) {
              const isFirstInGroup = group?.indexes?.[0] === position.index;

              if (!isFirstInGroup) return null;

              return (
                <div className="part-builder-optionCard" key={position.group}>
                  <div className="part-builder-optionHeader">
                    <div>
                      <h3>{group?.label || label}</h3>
                      <p>Positions {group?.indexes?.map((i) => i + 1).join(", ")}</p>
                    </div>

                    <strong>{group ? getGroupedValue(chars, group) || "—" : "—"}</strong>
                  </div>

                  {group?.description && <p className="part-builder-muted">{group.description}</p>}

                  {Array.isArray(group?.options) && group.options.length > 0 && (
                    <div className="part-builder-optionGrid">
                      {group.options.map((option) => (
                        <button
                          type="button"
                          key={option.code}
                          className={`part-builder-optionButton ${
                            getGroupedValue(chars, group) === option.code ? "is-selected" : ""
                          }`}
                          onClick={() => handleGroupedOptionClick(group, option.code)}
                        >
                          <span>{option.code}</span>
                          <small>{option.description}</small>
                        </button>
                      ))}
                    </div>
                  )}

                  {group?.allowNumeric && (
                    <p className="part-builder-muted">Enter any numeric value for this grouped field.</p>
                  )}
                </div>
              );
            }

            return (
              <div className="part-builder-optionCard" key={position.index}>
                <div className="part-builder-optionHeader">
                  <div>
                    <h3>{label}</h3>
                    <p>Position {position.index + 1}</p>
                  </div>

                  <strong>{currentChar || "—"}</strong>
                </div>

                {options.length > 0 && (
                  <div className="part-builder-optionGrid">
                    {options.map((option) => (
                      <button
                        type="button"
                        key={option.code}
                        className={`part-builder-optionButton ${currentChar === option.code ? "is-selected" : ""}`}
                        onClick={() => handleOptionClick(position.index, option.code)}
                      >
                        <span>{option.code}</span>
                        <small>
                          {option.description}

                          {activeSchemas.length > 1 && option.usedBy?.size > 0 && (
                            <>
                              <br />
                              Used by: {Array.from(option.usedBy).join(", ")}
                            </>
                          )}
                        </small>
                      </button>
                    ))}
                  </div>
                )}

                {position.optionTypes.has("A-Z") && (
                  <p className="part-builder-muted">Enter any letter A-Z for this position.</p>
                )}

                {position.optionTypes.has("0-9") && (
                  <p className="part-builder-muted">Enter any number 0-9 for this position.</p>
                )}
              </div>
            );
          })}
        </section>
      )}

      {showStickyBuilder && cleanPartNumber && (
        <div className="part-builder-stickyMini">
          {activeProduct?.image && (
            <div className="part-builder-stickyImageWrap">
              <img
                src={activeProduct.image}
                alt={activeProduct.product || activeSchemaForProduct?.productName || "Product"}
                className="part-builder-stickyImage"
              />
            </div>
          )}

          <div
            className="part-builder-stickyContent"
            style={{
              width: `${stickyBoxesPixelWidth}px`,
              maxWidth: "100%",
            }}
          >
            <div
              className="part-builder-stickyTop"
              style={{
                width: `${stickyBoxesPixelWidth}px`,
                maxWidth: "100%",
              }}
            >
              <h2 className="part-builder-stickyTitle">{activeSchemaForProduct?.productName || pageTitle}</h2>

              <button
                type="button"
                className="part-builder-copyButton part-builder-copyButton--sticky"
                onClick={handleCopyPartNumber}
                disabled={!cleanPartNumber}
                title="Copy part number"
              >
                <span className="part-builder-copyIcon">⧉</span>
                {copiedPartNumber ? "Copied" : "Copy"}
              </button>
            </div>

            <div className="part-builder-stickyBoxes">
              {visibleChars.map((char, index) => (
                <div className="part-builder-stickyBoxGroup" key={`sticky-${index}`}>
                  <input
                    ref={(el) => {
                      stickyInputRefs.current[index] = el;
                    }}
                    className="part-builder-stickyBox"
                    value={char}
                    maxLength={1}
                    onChange={(event) =>
                      updateChar(index, event.target.value, {
                        focusNext: true,
                        useStickyFocus: true,
                      })
                    }
                    onPaste={(event) =>
                      handlePaste(index, event, {
                        useStickyFocus: true,
                      })
                    }
                    onKeyDown={(event) =>
                      handleKeyDown(index, event, {
                        useStickyFocus: true,
                      })
                    }
                    aria-label={`Sticky character ${index + 1}`}
                  />

                  <small>{index + 1}</small>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
